import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, ChevronDown, Copy, Pause, Play, Radio, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const STATUS_STYLE = {
    ok: "border-emerald-500/25 bg-emerald-500/10 text-emerald-300 shadow-[0_0_12px_rgba(52,211,153,0.22)]",
    retry: "border-amber-500/25 bg-amber-500/10 text-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.20)]",
    fail: "border-rose-500/25 bg-rose-500/10 text-rose-300 shadow-[0_0_12px_rgba(251,113,133,0.22)]",
};

const styleFor = (s) => (s >= 200 && s < 300 ? STATUS_STYLE.ok : s === 429 ? STATUS_STYLE.retry : STATUS_STYLE.fail);

const latencyCls = (ms) =>
    ms == null ? "text-slate-500" : ms < 100 ? "text-emerald-400" : ms <= 500 ? "text-amber-400" : "text-rose-400";

const sourceLabel = (e) => {
    if (e.simulated) return "simulator";
    if (e.source === "console") return "dev console";
    if (e.source === "api") return "api intake";
    if (e.source === "agent") return "machine agent";
    if (e.source === "hook") return "webhook intake";
    return e.source || "live";
};

const TYPE_COLORS = [
    "text-cyan-300", "text-cyan-300", "text-purple-300", "text-teal-300",
    "text-sky-300", "text-fuchsia-300", "text-emerald-300", "text-amber-300",
];
const typeColor = (t = "") => {
    const ns = t.split(".")[0];
    let h = 0;
    for (let i = 0; i < ns.length; i++) h = (h * 31 + ns.charCodeAt(i)) >>> 0;
    return TYPE_COLORS[h % TYPE_COLORS.length];
};

const relTime = (iso) => {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
};

// event-count buckets over the last ~2 minutes → waveform bars
const BUCKETS = 24;
const BUCKET_S = 5;
const useBuckets = (events) =>
    useMemo(() => {
        const now = Date.now();
        const b = new Array(BUCKETS).fill(0);
        for (const e of events) {
            const age = (now - new Date(e.created_at).getTime()) / 1000;
            const i = BUCKETS - 1 - Math.floor(age / BUCKET_S);
            if (i >= 0 && i < BUCKETS) b[i] += 1;
        }
        return b;
    }, [events]);

const PulseWaveform = ({ events, live }) => {
    const buckets = useBuckets(events);
    const max = Math.max(1, ...buckets);
    return (
        <div
            data-testid="stream-pulse-waveform"
            className="flex h-9 items-end gap-[3px] overflow-hidden"
            aria-label="Event throughput over the last two minutes"
        >
            {buckets.map((n, i) => (
                <div
                    key={i}
                    className={`w-full rounded-sm transition-all duration-300 ease-out ${
                        n > 0
                            ? "bg-cyan-400/80 shadow-[0_0_8px_rgba(34,211,238,0.35)]"
                            : live
                              ? "bg-slate-800/70"
                              : "bg-slate-800/40"
                    }`}
                    style={{ height: `${Math.max(8, (n / max) * 100)}%` }}
                />
            ))}
        </div>
    );
};

const Vitals = ({ events }) => {
    const stats = useMemo(() => {
        const now = Date.now();
        const recent = events.filter((e) => now - new Date(e.created_at).getTime() < 60000);
        const ok = events.filter((e) => e.status < 400).length;
        const lat = events.map((e) => e.latency).filter((x) => x != null);
        return {
            perMin: recent.length,
            success: events.length ? Math.round((ok / events.length) * 100) : 100,
            avgLatency: lat.length ? Math.round(lat.reduce((a, b) => a + b, 0) / lat.length) : 0,
        };
    }, [events]);
    const Item = ({ label, value, cls = "text-slate-100", testid }) => (
        <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-600">{label}</div>
            <div data-testid={testid} className={`mt-0.5 font-mono text-sm font-bold ${cls}`}>{value}</div>
        </div>
    );
    return (
        <div data-testid="stream-vitals-ticker" className="flex items-center gap-5">
            <Item label="events/min" value={stats.perMin} cls="text-cyan-300" testid="stream-vitals-rate" />
            <Item
                label="success"
                value={`${stats.success}%`}
                cls={stats.success >= 95 ? "text-emerald-400" : stats.success >= 80 ? "text-amber-400" : "text-rose-400"}
                testid="stream-vitals-success"
            />
            <Item
                label="avg latency"
                value={`${stats.avgLatency}ms`}
                cls={stats.avgLatency < 100 ? "text-emerald-400" : stats.avgLatency <= 500 ? "text-amber-400" : "text-rose-400"}
                testid="stream-vitals-latency"
            />
        </div>
    );
};

const EventDetail = ({ e }) => {
    const [copied, setCopied] = useState(false);
    const payloadObj =
        e.payload != null
            ? e.payload
            : { type: e.type, endpoint: e.endpoint, status: e.status, latency_ms: e.latency, source: sourceLabel(e) };
    const payloadText = JSON.stringify(payloadObj, null, 2);

    const copyPayload = async () => {
        try {
            await navigator.clipboard.writeText(payloadText);
            setCopied(true);
            toast.success("Payload copied");
            setTimeout(() => setCopied(false), 1500);
        } catch {
            toast.error("Copy failed");
        }
    };

    return (
        <div
            data-testid="stream-detail"
            className="mt-1 space-y-3 rounded-md border border-slate-800/80 bg-slate-950/70 px-3.5 py-3"
        >
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[10px] sm:grid-cols-3">
                {[
                    ["event id", e.id],
                    ["type", e.type],
                    ["source", sourceLabel(e)],
                    ["endpoint", e.endpoint || "—"],
                    ["latency", `${e.latency ?? 0}ms`],
                    ["received", (e.created_at || "").replace("T", " ").slice(0, 19)],
                ].map(([k, v]) => (
                    <div key={k} className="min-w-0">
                        <div className="uppercase tracking-[0.18em] text-slate-600">{k}</div>
                        <div className="mt-0.5 truncate text-slate-300">{v}</div>
                    </div>
                ))}
            </div>

            <div>
                <div className="mb-1.5 flex items-center justify-between">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                        Payload
                    </span>
                    {payloadText && (
                        <button
                            data-testid="stream-payload-copy"
                            onClick={copyPayload}
                            className="flex items-center gap-1 rounded border border-slate-700 px-2 py-0.5 font-mono text-[9px] text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        >
                            {copied ? <Check className="h-2.5 w-2.5 text-emerald-400" /> : <Copy className="h-2.5 w-2.5" />}
                            {copied ? "copied" : "copy"}
                        </button>
                    )}
                </div>
                {payloadText ? (
                    <pre
                        data-testid="stream-payload"
                        className="max-h-52 overflow-auto whitespace-pre-wrap rounded-md border border-slate-800 bg-slate-950/90 p-3 font-mono text-[10.5px] leading-relaxed text-cyan-100"
                    >
                        {payloadText}
                    </pre>
                ) : null}
            </div>

            <div>
                <div className="mb-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">
                    Delivery timeline
                </div>
                <ol data-testid="stream-timeline" className="space-y-1.5">
                    <li className="flex items-center gap-2.5 font-mono text-[10px]">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" />
                        <span className="text-slate-500">{(e.created_at || "").slice(11, 19)}</span>
                        <span className="text-slate-300">Event accepted by MDH</span>
                    </li>
                    <li className="flex items-center gap-2.5 font-mono text-[10px]">
                        <span
                            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                e.status >= 200 && e.status < 300
                                    ? "bg-emerald-400"
                                    : e.status === 429
                                      ? "bg-amber-400"
                                      : "bg-rose-400"
                            }`}
                        />
                        <span className="text-slate-300">Delivered to {e.endpoint || "subscribers"}</span>
                        <span className={`rounded-full border px-2 py-px text-[9px] font-semibold ${styleFor(e.status)}`}>
                            {e.status === 429 ? "429 · RETRY" : e.status}
                        </span>
                        <span className="text-slate-600">{e.latency ?? 0}ms</span>
                    </li>
                    {e.status >= 400 && (
                        <li className="flex items-center gap-2.5 font-mono text-[10px]">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
                            <span className="text-amber-300">Queued for retry with exponential backoff</span>
                        </li>
                    )}
                </ol>
            </div>
        </div>
    );
};

export default function LiveStream({ events, live, appId }) {
    const [paused, setPaused] = useState(false);
    const [frozen, setFrozen] = useState(null);
    const [openId, setOpenId] = useState(null);
    const [query, setQuery] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [clearing, setClearing] = useState(false);

    const toggle = () => {
        if (!paused) setFrozen(events);
        else setFrozen(null);
        setPaused(!paused);
    };

    const clearStream = async () => {
        setClearing(true);
        try {
            const r = await fetch(`${API}/apps/${appId}/events`, {
                method: "DELETE",
                credentials: "include",
            });
            if (!r.ok) throw new Error("failed");
            const d = await r.json();
            toast.success(`Stream cleared — ${d.deleted} event${d.deleted === 1 ? "" : "s"} wiped`);
            setOpenId(null);
        } catch {
            toast.error("Could not clear the stream");
        } finally {
            setClearing(false);
            setConfirming(false);
        }
    };

    const q = query.trim().toLowerCase();
    const matches = (e) => {
        if (!q) return true;
        const hay = [
            e.type,
            e.endpoint,
            sourceLabel(e),
            String(e.status ?? ""),
            e.payload != null ? JSON.stringify(e.payload) : "",
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return q.split(/\s+/).every((term) => hay.includes(term));
    };

    const base = paused && frozen ? frozen : events;
    const shown = base.filter(matches);

    return (
        <div data-testid="dashboard-live-stream" className="glass-panel flex h-full flex-col overflow-hidden rounded-xl">
            {/* pulse strip: vitals + throughput waveform */}
            <div className="flex items-center gap-5 border-b border-slate-800/60 px-4 pb-2.5 pt-3">
                <Vitals events={base} />
                <div className="min-w-0 flex-1">
                    <PulseWaveform events={base} live={live} />
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800/80 px-4 py-2.5">
                <div className="flex shrink-0 items-center gap-2.5">
                    <span className="relative flex h-1.5 w-1.5">
                        {live && (
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                        )}
                        <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${live ? "bg-emerald-400" : "bg-slate-600"}`} />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        {live ? "Live event stream" : "Reconnecting…"}
                    </span>
                </div>
                <div className="relative order-3 w-full sm:order-2 sm:w-auto sm:min-w-0 sm:flex-1 sm:max-w-64">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-600" />
                    <input
                        data-testid="stream-search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Filter type, endpoint, payload…"
                        className="w-full rounded-md border border-slate-800 bg-slate-950/70 py-1.5 pl-7 pr-6 font-mono text-[10px] text-slate-200 placeholder:text-slate-600 focus:border-cyan-500/60 focus:outline-none"
                    />
                    {query && (
                        <button
                            data-testid="stream-search-clear"
                            onClick={() => setQuery("")}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-300"
                            aria-label="Clear filter"
                        >
                            <X className="h-3 w-3" />
                        </button>
                    )}
                </div>
                <div className="order-2 flex shrink-0 items-center gap-1.5 sm:order-3">
                    {!confirming ? (
                        <button
                            data-testid="stream-control-clear-button"
                            onClick={() => setConfirming(true)}
                            disabled={base.length === 0}
                            className="rounded border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:border-rose-500/50 hover:text-rose-300 disabled:opacity-40"
                            aria-label="Clear stream"
                        >
                            <Trash2 className="h-3 w-3" />
                        </button>
                    ) : (
                        <button
                            data-testid="stream-control-clear-confirm-button"
                            onClick={clearStream}
                            onBlur={() => setConfirming(false)}
                            disabled={clearing}
                            autoFocus
                            className="rounded border border-rose-500/50 bg-rose-950/40 px-2.5 py-1.5 font-mono text-[10px] font-semibold text-rose-300 transition-colors hover:bg-rose-950/70 disabled:opacity-50"
                        >
                            {clearing ? "wiping…" : `wipe ${base.length}?`}
                        </button>
                    )}
                    <button
                        data-testid="stream-pause-button"
                        onClick={toggle}
                        className="rounded border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                        aria-label={paused ? "Resume" : "Pause"}
                    >
                        {paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                    </button>
                </div>
            </div>
            <div className="h-[340px] space-y-1 overflow-y-auto px-2 py-2">
                {shown.length === 0 && (
                    <div
                        data-testid="stream-empty"
                        className="flex flex-col items-center gap-2.5 px-3 py-12 text-center"
                    >
                        {q ? (
                            <p className="font-mono text-[11px] leading-relaxed text-slate-600">
                                Nothing matches "{query}" in the {base.length} loaded events.
                            </p>
                        ) : (
                            <>
                                <Radio className="h-6 w-6 animate-pulse text-cyan-500/50" />
                                <p className="font-mono text-xs font-semibold text-slate-400">
                                    Listening for your first event
                                </p>
                                <p className="max-w-sm font-mono text-[11px] leading-relaxed text-slate-600">
                                    Publish one from the console below — type{" "}
                                    <span className="text-cyan-300">POST /apps/self/events/test {"{\"type\":\"hello\"}"}</span>
                                    {" "}— or fire the API intake, and it lands here instantly.
                                </p>
                            </>
                        )}
                    </div>
                )}
                {shown.map((e) => (
                    <motion.div
                        key={e.id}
                        initial={{ opacity: 0, x: -14 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <button
                            data-testid="stream-row"
                            onClick={() => setOpenId(openId === e.id ? null : e.id)}
                            className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left font-mono text-[11px] transition-colors hover:bg-slate-900/50 ${
                                openId === e.id ? "bg-slate-900/60" : ""
                            }`}
                        >
                            <div className="flex min-w-0 items-center gap-2.5">
                                <ChevronDown
                                    className={`h-3 w-3 shrink-0 text-slate-600 transition-transform ${
                                        openId === e.id ? "rotate-180" : ""
                                    }`}
                                />
                                <span className="hidden text-slate-600 sm:inline" title={e.created_at}>
                                    {relTime(e.created_at)}
                                </span>
                                <span className={`truncate ${typeColor(e.type)}`}>{e.type}</span>
                                {e.simulated && (
                                    <span className="rounded border border-slate-700 px-1 py-px text-[9px] text-slate-500">
                                        sim
                                    </span>
                                )}
                            </div>
                            <div className="flex shrink-0 items-center gap-2.5">
                                <span className="hidden max-w-[130px] truncate text-slate-500 md:inline">
                                    {e.endpoint}
                                </span>
                                <span className={latencyCls(e.latency)}>{e.latency}ms</span>
                                <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${styleFor(e.status)}`}>
                                    {e.status === 429 ? "429 · RETRY" : e.status}
                                </span>
                            </div>
                        </button>
                        {openId === e.id && <EventDetail e={e} />}
                    </motion.div>
                ))}
            </div>
        </div>
    );
}
